import { cn } from '@lemonenergy/lemonpie-3'
import { AnimatePresence, motion } from 'framer-motion'
import { useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'

import { toastAnimations } from './toastAnimations'
import { ToastContext } from './ToastProvider'

import waitTimeout from '~/utils/waitTimeout'

type ToastPosition = 'top' | 'bottom'

export interface ToastProps {
  isOpen?: boolean
  onClose: () => void
  timeBeforeClose?: number
  position?: ToastPosition
  children: React.ReactNode
  className?: string
}

const positionClasses = {
  top: 'top-[10%]',
  bottom: 'bottom-0',
}

const Toast: React.FC<ToastProps> = ({
  isOpen = false,
  onClose,
  timeBeforeClose = 5000,
  position = 'bottom',
  children,
  className,
  ...props
}) => {
  const toastContainerRef = useContext(ToastContext)

  useEffect(() => {
    if (!isOpen || !onClose) return

    waitTimeout(() => isOpen && onClose(), timeBeforeClose)
  }, [isOpen, onClose, timeBeforeClose])

  if (!toastContainerRef) return null

  const animation = toastAnimations[position]

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn(
            'fixed right-0 flex items-center gap-smaller bg-expressive-strongest rounded-[0.375rem] fg-inverted-main p-main m-0 mx-large mb-large shadow-[0_8px_32px_0_rgba(183,188,184,0.2)]',
            positionClasses[position],
            className,
          )}
          {...animation}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    toastContainerRef,
  )
}

export default Toast
